import { Link, useLocation } from 'react-router-dom';
import {
  destinoEnLaDemo,
  esEmpresaDemo,
  esEntradaAbiertaDeLaDemo,
  esRutaDelPlanCompletoEnLaDemo,
  seVeConCandadoEnLaDemo,
} from '@/features/mini-prixia/empresa-demo';
import { usePlanCompleto } from '@/features/mini-prixia/plan-completo-store';
import { useAuth } from '@/features/auth/hooks/use-auth';
import { cn } from '@/lib/utils/cn';
import { CUADRO_DE_MODULO, type Modulo } from './modulos';
import { MenuDeLaCuenta } from './menu-de-la-cuenta';
import {
  LayoutDashboard,
  BarChart3,
  DollarSign,
  FileText,
  FolderKanban,
  Boxes,
  Table2,
  CheckSquare,
  Database,
  BookOpen,
  HelpCircle,
  GraduationCap,
  ChevronLeft,
  ChevronRight,
  ChevronDown,
  Target,
  Code2,
  Globe,
  FilePlus,
  Package,
  Building2,
  Brain,
  Activity,
  Ship,
  Shield,
  Key,
  Store,
  History,
  Briefcase,
  Plug,
  Sun,
  ExternalLink,
  ShoppingBag,
  Lock,
  Gavel,
  Eye,
  Radar,
  Inbox,
  SlidersHorizontal,
  Receipt,
  Route as RouteIcon,
} from 'lucide-react';
import { useState, useCallback, useEffect, useMemo, type ReactNode } from 'react';
import { useSidebarStore } from '@/stores/sidebar-store';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip';
import { motion, AnimatePresence } from 'motion/react';
import { useApprovalStore } from '@/features/approvals/store/approval-store';
import { useAuthStore } from '@/features/auth/store/auth-store';
import { useTradeCounters } from '@/features/trade/hooks/use-trade-counters';
import { usePendientesDeDecidir } from '@/features/compite/hooks/use-pendientes-de-decidir';
import { useEmpresaStore } from '@/features/mi-empresa/store/empresa-store';
import { computeSectionHeaders, flattenSingleChild, resolveSectionLabels } from './nav-flatten';
import type { Permission } from '@/types';
import { LogoPrixia } from '@/components/layout/logo-prixia';

// ── Types ────────────────────────────────────────────────────────

interface NavChild {
  label: string;
  href: string;
  icon: any;
  badge?: number;
  description?: string;
  menuKey?: string;
  /** Un permiso funcional en vez de un módulo del menú (Reglas, desde el 26-sep). */
  permission?: Permission;
  /**
   * La entrada también se ve con este permiso aunque falte su `menuKey`
   * (P3-D10: «Estrategias de Mercado» con ver reglas, porque Reglas vive adentro).
   */
  oConPermiso?: Permission;
  /**
   * Visible si la persona tiene CUALQUIERA de estos módulos (Gestión junta
   * clientes, oportunidades, cotizaciones y contratos: alcanza con uno).
   */
  cualquieraDe?: string[];
  /**
   * Otras direcciones en las que esta entrada queda marcada: las pantallas que
   * viven adentro de ella y ya no tienen entrada propia (las de Gestión).
   */
  tambienActivoEn?: string[];
  roles?: string[];
  // Soporte de submenu anidado (sub-grupo expandible dentro de un grupo)
  children?: NavChild[];
  /**
   * Etiqueta de etapa que se dibuja ANTES de esta entrada.
   *
   * Sirve para agrupar por momento del trabajo dentro de un mismo módulo
   * ("cotizar" / "operar" / "configurar"), sin agregar un nivel de anidamiento
   * que obligaría a un clic más.
   */
  stageLabel?: string;
  /**
   * Estilo del contador. `warn` lo pinta en ámbar: se usa para lo que hay que
   * atender (tarifas vencidas), no para lo que simplemente hay.
   */
  badgeTone?: 'neutral' | 'warn';
}

interface NavGroup {
  id: string;
  label: string;
  icon: any;
  href?: string; // If set, clicking group label navigates here
  badge?: number;
  description?: string;
  children?: NavChild[];
  roles?: string[];
  permission?: Permission;
  menuKey?: string; // Key for dynamic menu visibility
  sectionLabel?: string; // If set, renders a section header before this group
  /**
   * Es un módulo del producto (Commercial, Compite, Trade): su ícono va en un
   * cuadro de color, como en los bocetos de Compite. El resto de las entradas
   * (datos, cuenta, ayuda) lleva el ícono suelto.
   */
  modulo?: boolean;
  /** Cuál de los tres módulos es: da el color de su cuadro (`modulos.ts`). */
  colorModulo?: Modulo;
}

/** Nombre corto del rol para la tarjeta de usuario. */
const ROL_CORTO: Record<string, string> = {
  PLATFORM_ADMIN: 'Superadmin',
  COMPANY_ADMIN: 'Admin',
  SALESPERSON: 'Vendedor',
  APPROVER: 'Aprobador',
};

// ── Ruta activa ──────────────────────────────────────────────────

/**
 * ¿La ruta actual corresponde a este enlace del menú?
 *
 * Cada sección de Trade Intelligence tiene su propia URL, así que alcanza con
 * comparar el path. El `startsWith` con la barra es lo que mantiene "Cotizaciones"
 * encendido mientras estás dentro de `/trade/cotizaciones/COT-123` — antes,
 * abrir una cotización te dejaba marcado en Import/Export.
 *
 * Se sigue admitiendo un href con query por si alguna entrada futura lo necesita:
 * en ese caso todos sus parámetros tienen que coincidir.
 */
export function isRouteActive(href: string, pathname: string, search: string): boolean {
  const [hrefPath, hrefQuery] = href.split('?');

  if (pathname !== hrefPath && !pathname.startsWith(`${hrefPath}/`)) return false;
  if (!hrefQuery) return true;

  const current = new URLSearchParams(search);
  const wanted = new URLSearchParams(hrefQuery);
  for (const [key, value] of wanted) {
    if (current.get(key) !== value) return false;
  }
  return true;
}

/**
 * De todos los destinos del menú, cuál es el que está abierto ahora.
 *
 * 🔴 El bug que arregla, y que se veía como "las letras se ponen blancas":
 *
 * `isRouteActive` acierta por PREFIJO, que es lo correcto para decidir si un
 * grupo se despliega. Pero para pintar la pastilla azul es demasiado generoso:
 * estando en `/admin/tenants` daba verdadero para "Empresas" **y también** para
 * "Panel Admin", cuyo destino es `/admin`. Dos entradas activas a la vez.
 *
 * Y ahí entra la segunda mitad. La pastilla se dibuja con
 * `layoutId="sidebar-active"`, el mismo para todas: Framer Motion las trata como
 * un único elemento que se mueve, así que **dibuja una sola**. La otra entrada se
 * quedaba con `text-primary-foreground` —letra blanca— y sin fondo azul debajo.
 * Letra blanca sobre fondo blanco: la entrada desaparecía.
 *
 * La regla es la misma que ya usa `route-modules.ts`: **gana el destino más
 * largo que coincida.** Estando en `/admin/tenants`, `/admin/tenants` (15) le gana
 * a `/admin` (6). Y estando en `/quotes/123`, "Cotizaciones" (`/quotes`) sigue
 * ganando porque no hay ningún destino más específico — que es lo que se quiere.
 */
export function resolverDestinoActivo(
  destinos: string[],
  pathname: string,
  search: string
): string | null {
  let ganador: string | null = null;
  for (const destino of destinos) {
    if (!isRouteActive(destino, pathname, search)) continue;
    if (ganador === null || destino.length > ganador.length) ganador = destino;
  }
  return ganador;
}

// ── Sidebar ──────────────────────────────────────────────────────

export function Sidebar() {
  const location = useLocation();
  const { user, isAdmin, canSeeMenu, hasPermission } = useAuth();
  // «Prixia × tu logo» (maqueta de Melanie): el logo de la empresa, si lo cargó.
  const logoDeLaEmpresa = useEmpresaStore((s) =>
    s.deTenant === user?.tenantId ? s.empresa?.logoUrl : null
  );
  const nombreDeLaEmpresa = useEmpresaStore((s) => s.empresa?.name);
  const cargarEmpresa = useEmpresaStore((s) => s.cargar);
  useEffect(() => {
    if (user?.tenantId) cargarEmpresa(user.tenantId).catch(() => {});
  }, [user?.tenantId, cargarEmpresa]);
  // Mi empresa › Apariencia: el degradado que eligió la empresa, en toda la app
  // (`html[data-apariencia]`, index.css). Al salir de la app vuelve el de Prixia.
  const apariencia = useEmpresaStore((s) =>
    s.deTenant === user?.tenantId ? s.empresa?.uiTheme : null
  );
  useEffect(() => {
    const raiz = document.documentElement;
    if (apariencia && apariencia !== 'prixia') raiz.dataset.apariencia = apariencia;
    else delete raiz.dataset.apariencia;
    return () => {
      delete raiz.dataset.apariencia;
    };
  }, [apariencia]);
  const [collapsed, setCollapsed] = useState(false);
  /*
    En teléfono el menú es un cajón que tapa la pantalla, y estaba SIEMPRE
    presente ocupando 224 de los 390 px del ancho: la tabla que había al lado
    mostraba dos columnas y el resto se alcanzaba con scroll horizontal.
  */
  const cajonAbierto = useSidebarStore((s) => s.abierto);
  const cerrarCajon = useSidebarStore((s) => s.cerrar);
  const [expanded, setExpanded] = useState<Set<string>>(() => new Set());
  /*
    Al navegar, el cajón se cierra. Sin esto uno toca "Empresas" y se queda
    mirando el menú, con la pantalla nueva detrás y sin señal de que cambió.
  */
  useEffect(() => {
    cerrarCajon();
  }, [location.pathname, cerrarCajon]);

  const approvalsStats = useApprovalStore((s) => s.stats);
  const fetchApprovalStats = useApprovalStore((s) => s.fetchStats);
  // Sólo quien puede aprobar tiene estadísticas de aprobaciones: al resto el servidor le contesta 403.
  // 2.7b: y sólo si el módulo está en el menú (el mismo criterio): sin Cotiza en el techo el servidor da 403.
  const puedeAprobar = hasPermission('approve_quotes') && canSeeMenu('approvals');
  useEffect(() => {
    if (!puedeAprobar) return;
    fetchApprovalStats();
    const t = setInterval(fetchApprovalStats, 60000);
    return () => clearInterval(t);
  }, [fetchApprovalStats, puedeAprobar]);
  const pendingApprovalsCount = approvalsStats?.pending ?? 0;

  // Contadores de Trade Intelligence. Solo se piden si el módulo es visible.
  const tradeCounters = useTradeCounters(canSeeMenu('trade-cotizador'));

  // Compite › Decisiones: cuántos precios esperan una decisión (maqueta del 14-oct).
  // Se vuelve a pedir al navegar, y Revisar precios lo avisa al decidir.
  const pendientesDeDecidir = usePendientesDeDecidir(canSeeMenu('data-import'), location.pathname);

  // Toggle a group open/closed
  const toggleGroup = useCallback((id: string) => {
    setExpanded((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }, []);

  // Auto-expand group if a child (or any nested sub-child) route is active
  const isChildActive = (children?: NavChild[]): boolean => {
    if (!children) return false;
    return children.some((c) => {
      if (isRouteActive(c.href, location.pathname, location.search)) return true;
      // Las pantallas que viven adentro de la entrada (las de Gestión) también abren su grupo.
      if (
        c.tambienActivoEn?.some((otra) => isRouteActive(otra, location.pathname, location.search))
      )
        return true;
      if (c.children && c.children.length > 0) return isChildActive(c.children);
      return false;
    });
  };

  // ── Navigation structure ─────────────────────────────────────

  const navGroups: NavGroup[] = [
    // ── General ──
    {
      id: 'dashboard',
      // Rediseño del 18-oct: los nombres de la reunión del 14-oct (Inicio, Cotiza,
      // Compite, Comercia). Las direcciones y los permisos no cambian.
      label: 'Inicio',
      icon: LayoutDashboard,
      href: '/dashboard',
      menuKey: 'dashboard',
      description: 'Resumen de KPIs, cotizaciones recientes y tipo de cambio',
    },
    // ── Comercial ── (workflow del vendedor)
    {
      id: 'comercial',
      label: 'Cotiza',
      icon: Briefcase,
      sectionLabel: 'Módulos',
      modulo: true,
      colorModulo: 'cotiza',
      description: 'Cotizaciones, aprobaciones, contratos y clientes',
      children: [
        // 18-oct (artefacto): el Dashboard de cotizaciones de siempre.
        {
          label: 'Visión general',
          href: '/cotiza/vision-general',
          icon: LayoutDashboard,
          description: 'Indicadores, tendencia y pronóstico de tus cotizaciones',
          menuKey: 'dashboard',
        },
        {
          label: 'Nueva Cotización',
          href: '/quotes/new',
          icon: FilePlus,
          description: 'Crear una cotización desde cero',
          menuKey: 'nueva-cotizacion',
        },
        /*
          19-oct (artefacto «Gestión»): Clientes, Oportunidades, Cotizaciones
          (las del Pricer Datec y las generales) y Contratos dejan de ser cinco
          entradas sueltas y viven en Gestión, como los pasos de una venta. Cada
          pantalla sigue con su dirección; esta entrada queda marcada en todas.
        */
        {
          label: 'Gestión',
          href: '/cotiza/gestion',
          icon: FolderKanban,
          description: 'Clientes, oportunidades, cotizaciones y contratos en un solo lugar',
          cualquieraDe: ['customers', 'opportunities', 'quotes', 'pricer-datec', 'contracts'],
          tambienActivoEn: [
            '/customers',
            '/opportunities',
            '/quotes',
            '/pricer-datec',
            '/contracts',
          ],
        },
        {
          label: 'Aprobaciones',
          href: '/approvals',
          icon: CheckSquare,
          description: 'Revisar y aprobar/rechazar cotizaciones pendientes',
          menuKey: 'approvals',
          badge: pendingApprovalsCount,
        },
        {
          label: 'Modelos',
          href: '/pricing-models',
          icon: DollarSign,
          description: 'Configurar modelos con variables y fórmulas',
          menuKey: 'pricing',
        },
        {
          label: 'Cotizador Solar',
          href: '/pricer-solar',
          icon: Sun,
          description:
            'Calculadora de sistemas fotovoltaicos On-Grid (Bolivia). Dimensionamiento + análisis financiero (ROI, TIR, payback 25 años).',
          menuKey: 'pricer-solar',
        },
        {
          label: 'Inteligencia de Pricing',
          href: '/ai-intelligence',
          icon: Brain,
          description: 'Simulaciones, predicciones y recomendaciones con IA',
          menuKey: 'ai-intelligence',
        },
        // 18-oct: era una entrada suelta entre los módulos; es el análisis de
        // los márgenes de las cotizaciones, así que vive en Cotiza (artefacto).
        {
          label: 'Analítica',
          href: '/analytics',
          icon: BarChart3,
          description: 'Análisis detallado de márgenes, tendencias y rendimiento',
          menuKey: 'analytics',
        },
      ],
    },
    // ── Compite ── (ex Price Intelligence: todo lo que define el precio)
    //
    // Rediseño de Compite (docs/frontend/compite-arquitectura.md): las cuatro
    // vistas de lo que era Precios Dinámicos (que ahora redirige a Monitoreo) y
    // las dos de lo que era Estrategias de Mercado (Decisiones y Estrategia, de
    // Joaquín; `/precio-inteligente` redirige). El orden es el de la maqueta.
    {
      id: 'pricing-ai',
      label: 'Compite',
      icon: Radar,
      modulo: true,
      colorModulo: 'compite',
      description: 'Tus precios contra la competencia: monitoreo, estrategias y reglas',
      children: [
        {
          // Joaquín: lo que era Estrategias de Mercado › Hoy y Por decidir.
          stageLabel: 'día a día',
          label: 'Decisiones',
          href: '/compite/decisiones',
          icon: Inbox,
          description: 'Los precios que esperan tu sí y cuánta plata hay en juego',
          menuKey: 'data-import',
          badge: pendientesDeDecidir,
          badgeTone: 'warn',
        },
        {
          label: 'Monitoreo',
          href: '/compite/monitoreo',
          icon: Eye,
          description: 'Tus precios contra cada tienda y qué cambió en la competencia',
          menuKey: 'pricing',
        },
        {
          // No «Analítica»: ya hay una entrada con ese nombre (márgenes, /analytics).
          label: 'Analítica',
          href: '/compite/analitica',
          icon: Activity,
          description: 'Cómo evolucionan tus precios y los de la competencia',
          menuKey: 'pricing',
        },
        {
          // Joaquín: lo que era Estrategias de Mercado › Estrategias y Reglas.
          stageLabel: 'configuración',
          label: 'Estrategia',
          href: '/compite/estrategia',
          icon: SlidersHorizontal,
          description: 'Las estrategias que proponen precios y las reglas que los limitan',
          menuKey: 'data-import',
          // P3-D10 (27-sep): Reglas vive adentro; quien ve reglas llega por acá.
          oConPermiso: 'view_pricing_rules',
        },
        {
          label: 'Productos',
          href: '/compite/productos',
          icon: Boxes,
          description: 'Tu catálogo, el de cada tienda y los vínculos entre los dos',
          menuKey: 'pricing',
        },
        {
          label: 'Competencia',
          href: '/compite/competencia',
          icon: Store,
          description: 'Las tiendas contra las que te comparas y cuándo se leen',
          menuKey: 'pricing',
        },
      ],
    },
    // ── Comercia ──
    //
    // Rediseño de Melanie (reunión del 27-oct): tres pasos y nada más —«yo te
    // voy a decir cuánto cuesta traerlo acá, esa es mi solución»—. Producto,
    // Logística (origen y destino, carga y rutas) y Costo nacionalizado.
    //
    // Lo operativo (embarques, contenedores en tránsito, indicadores) salió del
    // menú a propósito: «nosotros no hacemos operativa». Sus pantallas siguen
    // existiendo con su dirección; el Tarifario pasó a Datos Maestros, que es lo
    // que es (lo actualiza el agente, no se toca en cada importación).
    {
      id: 'import-export',
      label: 'Comercia',
      icon: Globe,
      modulo: true,
      colorModulo: 'comercia',
      description: 'Lo que cuesta de verdad importar: producto, logística y costo nacionalizado',
      menuKey: 'import-tracking',
      children: [
        {
          label: 'Producto',
          href: '/comercia/productos',
          icon: Package,
          description: 'Lo que vas a traer: proforma, productos y su partida arancelaria',
        },
        {
          label: 'Logística',
          href: '/comercia/logistica/origen-destino',
          icon: RouteIcon,
          description: 'Origen y destino, la carga en el contenedor y la ruta con su costo',
          tambienActivoEn: ['/comercia/logistica'],
        },
        {
          label: 'Costo nacionalizado',
          href: '/comercia/costo-nacionalizado',
          icon: Receipt,
          description: 'Mercadería, logística y tributos, repartidos por producto',
        },
      ],
    },
    // ── Datos Maestros ── (cada entidad como ruta independiente)
    {
      id: 'master-data',
      label: 'Datos Maestros',
      icon: Database,
      sectionLabel: 'Datos y cuenta',
      description: 'Materiales, divisas, LOBs, países, fletes y más — cada entidad por separado',
      menuKey: 'master-data',
      children: [
        // 18-oct: era el grupo «Catálogo» entre los módulos (artefacto: va con los datos).
        {
          label: 'Materiales',
          href: '/materials',
          icon: Package,
          description: 'Catálogo de materiales y productos',
          menuKey: 'master-data',
        },
        {
          label: 'Divisas',
          href: '/master-data/currencies',
          icon: DollarSign,
          description: 'Catálogo de divisas activas',
          menuKey: 'master-data',
        },
        {
          label: 'Tipos de cambio',
          href: '/master-data/exchange-rates',
          icon: Activity,
          description: 'Historial y administración de tipos de cambio por fecha',
          menuKey: 'master-data',
        },
        {
          label: 'Líneas de negocio',
          href: '/master-data/lobs',
          icon: Briefcase,
          description: 'Sectores, verticales y marcas (Lines of Business)',
          menuKey: 'master-data',
        },
        {
          label: 'Países',
          href: '/master-data/countries',
          icon: Globe,
          description: 'Catálogo de países',
          menuKey: 'master-data',
        },
        /*
          🔴 Acá había una segunda entrada "Materiales", apuntando a
          `/master-data/materials` — la pestaña Materiales de Datos Maestros, que
          repite la pantalla propia de `/materials` (y es la única con «Importar
          CSV»). Dos entradas con el mismo nombre confundían.

          Se quitó en vez de arreglarle el destino: Materiales ya está más arriba
          en el menú, como entrada propia, y es de las que se usan todos los días.
          Dos entradas con el mismo nombre y el mismo destino solo obligan a
          preguntarse en qué se diferencian.
        */
        {
          label: 'Tipos de cotización',
          href: '/master-data/quote-types',
          icon: FileText,
          description: 'Categorías de cotizaciones',
          menuKey: 'master-data',
        },
        {
          label: 'Centros de beneficio',
          href: '/master-data/profit-centers',
          icon: Target,
          description: 'Profit centers / CeBe',
          menuKey: 'master-data',
        },
        {
          label: 'Incoterms',
          href: '/master-data/incoterms',
          icon: Shield,
          description: 'Configuración de Incoterms y matriz de costos',
          menuKey: 'master-data',
        },

        /*
          Los catálogos del flete, que venían colgados del Tarifario.

          Las cuatro páginas siempre vivieron en `/master-data/…`; lo que
          cambia es dónde se las nombra. Van juntas bajo su propio encabezado
          para que no se mezclen con Divisas y LOBs.

          Conservan `menuKey: 'trade-tarifario'` a propósito: quien administra
          el tarifario tiene que poder mantener sus proveedores y puertos, y
          atarlas a `master-data` le daría acceso a Divisas y Centros de
          Beneficio, que no tienen nada que ver.
        */
        {
          /*
            28-oct (Comercia en tres pasos): el Tarifario se mudó acá desde
            Comercia. Es dato maestro —lo sube el agente y vence por semana—, y
            Comercia › Rutas lo usa y avisa cuando está vencido. Conserva su
            contador de vencidas.
          */
          stageLabel: 'flete',
          label: 'Tarifario',
          href: '/trade/tarifario',
          icon: Table2,
          description: 'Subir el Excel de la semana y ver la vigencia de las tarifas',
          menuKey: 'trade-tarifario',
          badge: tradeCounters.expiredRates,
          badgeTone: 'warn',
        },
        {
          label: 'Tramos y tarifas',
          href: '/master-data/freight-leg-catalog',
          icon: Table2,
          description: 'Tramos catalogados con sus tarifas y vigencias',
          menuKey: 'trade-tarifario',
        },
        {
          label: 'Proveedores de flete',
          href: '/master-data/freight-providers',
          icon: Ship,
          description: 'Navieras, agentes de carga y transportistas',
          menuKey: 'trade-tarifario',
        },
        {
          label: 'Servicios de flete',
          href: '/master-data/freight-services',
          icon: Ship,
          description: 'Catálogo de servicios y cargos adicionales',
          menuKey: 'trade-tarifario',
        },
        {
          label: 'Ubicaciones',
          href: '/master-data/freight-locations',
          icon: Globe,
          description: 'Puertos, terminales y ciudades de origen y destino',
          menuKey: 'trade-tarifario',
        },
      ],
    },
    // (Sección Import / Export duplicada eliminada — unificada arriba.)
    /*
      ── Integraciones ── (unificada: SAP + tiendas + API)

      `menuKey` estaba ausente en este grupo y en todos sus hijos, así que era el
      ÚNICO que el sistema de permisos no podía ocultar: se filtraba solo por rol,
      y un COMPANY_ADMIN lo veía siempre.

      El efecto era peor que no tener la opción: un administrador desmarcaba
      "Integraciones" en la pantalla de permisos, guardaba, y el módulo seguía
      apareciendo. La configuración mentía.
    */
    {
      id: 'integraciones',
      label: 'Integraciones',
      icon: Plug,
      menuKey: 'integrations',
      description: 'SAP, tiendas online, API REST y API Keys',
      roles: ['COMPANY_ADMIN', 'PLATFORM_ADMIN'],
      children: [
        {
          label: 'SAP y CRM',
          href: '/integrations',
          icon: Database,
          description:
            'Configurar integraciones SAP S/4HANA, SAP C4C y HubSpot CRM, ejecutar sync, ver logs',
          roles: ['COMPANY_ADMIN', 'PLATFORM_ADMIN'],
        },
        {
          label: 'Tiendas conectadas',
          href: '/tiendas-conectadas',
          icon: Store,
          description: 'Gestionar conexiones a tiendas online (Shopify, VTEX, Magento, etc.)',
          // Ronda E de L7 (T16-2): el servidor las guarda con Precios Dinámicos, donde
          // se aplican los precios; sin el módulo, las dos entradas no se ofrecen.
          menuKey: 'pricing',
          roles: ['COMPANY_ADMIN', 'PLATFORM_ADMIN'],
        },
        {
          label: 'Historial de envíos',
          href: '/historial-push',
          icon: History,
          description: 'Registro de envíos de precios a tiendas online',
          menuKey: 'pricing',
          roles: ['COMPANY_ADMIN', 'PLATFORM_ADMIN'],
        },
        {
          label: 'Integración API',
          href: '/integracion-api',
          icon: Code2,
          description: 'Documentación y configuración de la integración API REST',
          roles: ['COMPANY_ADMIN', 'PLATFORM_ADMIN'],
        },
        {
          label: 'Claves API',
          href: '/claves-api',
          icon: Key,
          description: 'Gestionar API Keys para integraciones externas',
          roles: ['COMPANY_ADMIN', 'PLATFORM_ADMIN'],
        },
      ],
    },
    // ── Sistema ──
    {
      id: 'admin',
      label: 'Mi empresa',
      /*
        🔴 Este grupo TIENE que abrir su propia sección, aunque el rótulo termine
        sin dibujarse por repetir el nombre del grupo.

        Sin esto, `resolveSectionLabels` arrastra el rótulo pendiente al siguiente
        grupo visible. Para quien NO es admin de plataforma, el grupo Plataforma se
        filtra y su rótulo —"PLATAFORMA · DATEC"— caía encima de Mi empresa:
        etiquetaba la sección de la PROPIA empresa del cliente como si fuera la de
        DATEC. Visto en dev con un COMPANY_ADMIN.

        Antes no molestaba porque el rótulo decía "Sistema" y cubría los dos grupos
        a propósito. Al volverlo específico de plataforma, dejó de poder heredarse.
      */
      /*
        Maqueta de Melanie (25-oct): un solo enlace. Usuarios, Roles, Equipos,
        Aprobaciones, Documentos y Pagos son pestañas de /mi-empresa;
        Configuración queda en el menú de la cuenta y en el buscador.
      */
      icon: Building2,
      menuKey: 'admin',
      href: '/mi-empresa',
      description: 'Tu empresa: usuarios, roles, equipos, aprobaciones, documentos y pagos',
    },
    // ── Demo ── (link al ecommerce demo desplegado en la VM)
    // Solo admins: es un link de demostración, no parte del flujo de un usuario
    // operativo (p.ej. roles custom como "Usuarios Datec Ltda" no deben verlo).
    // La dirección sale de VITE_TIENDA_DEMO_URL (25-oct: en local, la tienda de
    // Farmacia San Jorge, packages/tienda-farmacia); sin ella, la de la VM.
    {
      id: 'demo',
      label: 'Tienda online',
      icon: ShoppingBag,
      href: import.meta.env.VITE_TIENDA_DEMO_URL || 'https://prixia.datec.com.bo/tienda/',
      description: 'La tienda online de ejemplo (abre en nueva pestaña)',
      // Sin `menuKey` solo se filtraba por rol, así que un COMPANY_ADMIN lo veía
      // siempre y el admin no podía sacarlo desde la pantalla de permisos —
      // el mismo hueco que tenía Integraciones.
      menuKey: 'demo-store',
      roles: ['COMPANY_ADMIN', 'PLATFORM_ADMIN'],
    },
    // ── Ayuda ──
    {
      id: 'help',
      label: 'Ayuda',
      icon: HelpCircle,
      // Sección propia: sin esto heredaba la de arriba y quedaba bajo "DEMO",
      // que no lo describe. El encabezado no se dibuja porque repetiría el
      // nombre del grupo — ver `computeSectionHeaders`.
      menuKey: 'tutorial',
      description: 'Centro de ayuda, manual y tutoriales',
      children: [
        {
          label: 'Centro de Ayuda',
          href: '/help',
          icon: BookOpen,
          description: 'Manual, FAQ, inicio rápido y SLA',
        },
        {
          label: 'Curso Interactivo',
          href: '/help/curso-implementacion',
          icon: GraduationCap,
          description: 'Curso de implementación con audio narrado y hotspots sincronizados',
        },
        {
          label: 'Tutorial',
          href: '/tutorial',
          icon: GraduationCap,
          description: 'Guia paso a paso para aprender a usar la plataforma',
        },
      ],
    },
  ];

  // Determina si un item (grupo o hijo) es visible para el usuario actual.
  // Si tiene roles, chequea contra el role del user. Si tiene menuKey, chequea
  // via canSeeMenu. Sin filtros = visible.
  const itemVisible = (item: {
    roles?: string[];
    menuKey?: string;
    permission?: Permission;
    oConPermiso?: Permission;
    cualquieraDe?: string[];
  }): boolean => {
    if (item.roles && !item.roles.includes(user?.role || '')) return false;
    if (item.cualquieraDe && !item.cualquieraDe.some((k) => canSeeMenu(k))) return false;
    if (
      item.menuKey &&
      !canSeeMenu(item.menuKey) &&
      !(item.oConPermiso && hasPermission(item.oConPermiso)) &&
      // Mini Prixia: lo que la demo no incluye se ve, con candado (ver NavLink); los
      // productos hechos para otros clientes no se muestran (I3 de la revisión).
      !(esEmpresaDemo() && seVeConCandadoEnLaDemo(item.menuKey))
    )
      return false;
    if (item.permission && !hasPermission(item.permission)) return false;
    return true;
  };

  // Filtra los hijos de un grupo por roles + menuKey. Si un child es un
  // sub-grupo (tiene children), filtra recursivamente y lo oculta si no
  // queda ningun sub-child visible.
  const filterChildren = (group: NavGroup): NavChild[] | undefined => {
    if (!group.children) return undefined;
    return group.children
      .filter(itemVisible)
      .map((child) => {
        if (!child.children) return child;
        const visibleSubs = child.children.filter(itemVisible);
        if (visibleSubs.length === 0) return null;
        return { ...child, children: visibleSubs };
      })
      .filter((c): c is NavChild => c !== null);
  };

  // Un grupo se muestra si el grupo en sí es visible y, si tiene hijos, al
  // menos uno de ellos es visible. Esto evita mostrar el header de un grupo
  // cuyo contenido entero quedó filtrado.
  const canViewGroup = (group: NavGroup): boolean => {
    if (!itemVisible(group)) return false;
    if (group.children && group.children.length > 0) {
      return group.children.some(itemVisible);
    }
    return true;
  };

  /*
    🔴 «Mi empresa» la ve TAMBIEN el superadmin. No agregar acá un filtro por rol.

    Estuvo escondida para PLATFORM_ADMIN (`a51ba4dd`, PR #78) con el argumento de
    que su empresa es una de la lista del panel, y que si necesita trabajar dentro
    de una usa «Entrar». Ese argumento tenía sentido cuando el panel de plataforma
    era un grupo de ESTE menú y los pares se pisaban (Usuarios / Usuarios de
    clientes, Configuracion / Configuracion). El panel ya salió del portal y tiene
    barra propia, así que no hay nada que se pise — y el filtro quedó sacándole al
    superadmin las seis entradas del grupo: Usuarios, Roles y Permisos, Equipos,
    Configuración, API Developers y Estado Proyecto.

    Medido en el navegador: el superadmin entraba a /settings y /permisos sólo
    escribiendo la URL a mano; Equipos no tenía forma de encontrarse, así que la
    pantalla de asignar equipos —que existe y funciona— era inalcanzable.

    Y el superadmin de Prixia es además el admin de DATEC: esconderle su propia
    empresa no le tapa una duplicación, le tapa su trabajo diario.
  */

  // El menú se resuelve entero antes de dibujarlo, y el orden importa: qué
  // queda visible → a quién le toca cada encabezado de sección → qué grupos se
  // aplanan → qué encabezados vale la pena dibujar. Cada paso depende del
  // anterior, porque todos se deciden por lo que quedó a la vista y no por lo
  // que el código declara.
  const preparedGroups = resolveSectionLabels(navGroups, canViewGroup).map(
    ({ group, sectionLabel }) =>
      flattenSingleChild({ ...group, sectionLabel }, filterChildren(group))
  );
  /*
    Todos los destinos que el menú está dibujando ahora mismo, para poder elegir
    el más específico. Se arma desde `preparedGroups` y no de una lista aparte
    escrita a mano: una lista aparte se desactualiza el día que alguien agrega
    una entrada, y el bug vuelve sin que nadie lo note.
  */
  const { destinosDelMenu, alias } = useMemo(() => {
    const destinos: string[] = [];
    // Dirección de una pantalla de adentro → la entrada que la representa.
    const alias = new Map<string, string>();
    const juntar = (
      hijos?: Array<{ href?: string; children?: any[]; tambienActivoEn?: string[] }>
    ) => {
      for (const hijo of hijos ?? []) {
        if (hijo.href) destinos.push(hijo.href);
        for (const otra of hijo.tambienActivoEn ?? []) {
          if (hijo.href && !alias.has(otra)) {
            alias.set(otra, hijo.href);
            destinos.push(otra);
          }
        }
        if (hijo.children) juntar(hijo.children);
      }
    };
    for (const { group, children } of preparedGroups) {
      if (group.href) destinos.push(group.href);
      juntar(children);
    }
    return { destinosDelMenu: destinos, alias };
  }, [preparedGroups]);

  const destinoMasPreciso = resolverDestinoActivo(
    destinosDelMenu,
    location.pathname,
    location.search
  );
  const destinoActivo = destinoMasPreciso
    ? (alias.get(destinoMasPreciso) ?? destinoMasPreciso)
    : null;

  const sectionHeaders = computeSectionHeaders(
    preparedGroups.map(({ group, children }) => ({
      sectionLabel: group.sectionLabel,
      label: group.label,
      hasChildren: !!children && children.length > 0,
    }))
  );

  return (
    <>
      {/*
        La cortina detrás del cajón, solo en teléfono. Cumple dos funciones: deja
        claro que el menú está por encima de la página, y da una forma obvia de
        cerrarlo tocando al costado — que es como se cierra un cajón en un
        teléfono, sin buscar la X.
      */}
      {cajonAbierto && (
        <div
          className="fixed inset-0 z-40 bg-black/50 md:hidden"
          onClick={cerrarCajon}
          aria-hidden="true"
        />
      )}

      <aside
        className={cn(
          /*
            `tema-compite` en el menú (15-oct): Inter, tamaños reales y radios
            amables, como en los bocetos. Sólo forma: los colores son los de
            siempre (lo vigila `tema-compite-sin-colores.test.ts`).

            La transición nombra lo que cambia (plan 175 · B2): el ANCHO al
            colapsar en escritorio y el DESPLAZAMIENTO del cajón en teléfono.
          */
          'tema-compite bg-card border-r border-border/70 flex flex-col transition-[width,transform] duration-300',
          /*
            · teléfono  → `fixed`, por encima de todo, entra y sale deslizándose.
            · md o más  → una columna en el flujo; su ancho lo decide `collapsed`.
            Anchos en px y no en rem: la raíz de la app es de 14 px y `w-64`
            daría 224 px en vez de los 252 del boceto.
          */
          'fixed inset-y-0 left-0 z-50 w-[252px]',
          cajonAbierto ? 'translate-x-0' : '-translate-x-full',
          'md:static md:z-auto md:translate-x-0 md:h-screen',
          collapsed ? 'md:w-[68px]' : 'md:w-[252px]'
        )}
      >
        {/* Logo de Prixia y, si la empresa cargó el suyo, «× su logo» (maqueta del 25-oct). */}
        <div
          className={cn(
            'h-16 shrink-0 flex items-center justify-between',
            collapsed ? 'px-2' : 'pl-5 pr-3'
          )}
        >
          <AnimatePresence mode="wait">
            {!collapsed && (
              <motion.div
                key="logo"
                initial={{ opacity: 0, x: -10 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: -10 }}
                transition={{ duration: 0.2 }}
                className="flex min-w-0 items-center gap-2"
              >
                <LogoPrixia className="h-7 w-auto shrink-0" />
                {logoDeLaEmpresa && (
                  <>
                    <span className="text-xs font-medium text-muted-foreground" aria-hidden="true">
                      ×
                    </span>
                    <img
                      src={logoDeLaEmpresa}
                      alt={nombreDeLaEmpresa ?? 'Logo de la empresa'}
                      // Sobre blanco también en oscuro: un logo con letras oscuras no se leía.
                      className="h-6 max-w-[78px] rounded bg-white object-contain px-1 py-px"
                    />
                  </>
                )}
              </motion.div>
            )}
          </AnimatePresence>
          <Tooltip>
            <TooltipTrigger asChild>
              <Button
                variant="ghost"
                size="icon"
                onClick={() => setCollapsed(!collapsed)}
                className={cn(
                  'h-8 w-8 rounded-lg text-muted-foreground',
                  collapsed ? 'mx-auto' : 'ml-auto'
                )}
              >
                {collapsed ? (
                  <ChevronRight className="w-4 h-4" />
                ) : (
                  <ChevronLeft className="w-4 h-4" />
                )}
              </Button>
            </TooltipTrigger>
            <TooltipContent side="right">
              {collapsed ? 'Expandir menu lateral' : 'Colapsar menu lateral'}
            </TooltipContent>
          </Tooltip>
        </div>

        {/* Navigation */}
        <nav
          className={cn(
            'flex-1 overflow-y-auto pb-6 space-y-1 relative',
            // El último ítem que asoma se desvanece en lugar de verse cortado por el plan.
            '[mask-image:linear-gradient(to_bottom,black_calc(100%-1.5rem),transparent)]',
            collapsed ? 'px-2' : 'px-3'
          )}
        >
          {preparedGroups.map(({ group, children }, groupIndex) => {
            const hasChildren = children && children.length > 0;
            const showSection = sectionHeaders[groupIndex];
            // El grupo se pinta activo solo si ÉL es el destino más específico.
            const groupActive = group.href ? group.href === destinoActivo : isChildActive(children);
            const isOpen = expanded.has(group.id) || isChildActive(children);

            /*
              La sección de plataforma se marca distinto del resto del menú: todo
              lo que hay ahí adentro actúa **sobre otra empresa**. El color es
              `--plataforma`, un acento reservado a "estás fuera de tu casa".
            */
            // Sólo lo que es EXCLUSIVO de plataforma. Integraciones y Demo Store
            // también admiten PLATFORM_ADMIN, pero son de la empresa: con
            // `includes` se llevaban la banda roja sin motivo.
            const esPlataforma = group.roles?.length === 1 && group.roles[0] === 'PLATFORM_ADMIN';

            return (
              <div
                key={group.id}
                className={cn(esPlataforma && !collapsed && 'banda-plataforma rounded-r-md py-1')}
              >
                {showSection && !collapsed && (
                  <div className={cn('px-3 pb-2', groupIndex === 0 ? 'pt-2' : 'pt-5')}>
                    <div className="flex items-center gap-2">
                      {esPlataforma && (
                        <Gavel className="h-3 w-3 text-plataforma" aria-hidden="true" />
                      )}
                      <span
                        className={cn(
                          'text-[11px] font-semibold uppercase tracking-[0.1em]',
                          esPlataforma ? 'text-plataforma' : 'text-muted-foreground'
                        )}
                      >
                        {group.sectionLabel}
                      </span>
                    </div>
                  </div>
                )}
                {/*
                  Colapsado no hay lugar para el texto, así que la separación la
                  hace la línea.
                */}
                {showSection && collapsed && (
                  <div
                    className={cn(
                      'my-3 mx-2 h-px',
                      esPlataforma ? 'bg-plataforma/60' : 'bg-border'
                    )}
                  />
                )}
                <NavGroupItem
                  group={group}
                  children={children}
                  destinoActivo={destinoActivo}
                  hasChildren={!!hasChildren}
                  isOpen={!!isOpen}
                  isGroupActive={!!groupActive}
                  collapsed={collapsed}
                  pathname={location.pathname}
                  search={location.search}
                  onToggle={() => toggleGroup(group.id)}
                />
              </div>
            );
          })}
        </nav>

        {/*
          El plan, sus límites y los días que le quedan ya no van en el menú (pedido de
          Joaquín, 26-oct): se ven en Mi empresa › Resumen y en Pagos y facturación.
        */}

        {/*
          Tarjeta de usuario. Rediseño del 18-oct (artefacto): abre el menú de la
          cuenta (Mi perfil, Configuración, panel de plataforma, Cerrar sesión),
          que antes estaba en la barra de arriba. Con el menú achicado, el avatar.
        */}
        {user && (
          <div className={cn('border-t border-border/70', collapsed ? 'p-2' : 'p-2.5')}>
            <MenuDeLaCuenta lado={collapsed ? 'right' : 'top'}>
              <button
                type="button"
                aria-label={`Cuenta de ${user.firstName} ${user.lastName}`}
                className={cn(
                  'flex w-full items-center gap-3 rounded-xl text-left transition-colors duration-snap ease-snap hover:bg-muted focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring',
                  collapsed ? 'justify-center p-1' : 'px-2 py-2'
                )}
              >
                <span className="grid h-9 w-9 shrink-0 place-items-center rounded-full bg-primary text-[12px] font-semibold text-primary-foreground">
                  {user.firstName[0]}
                  {user.lastName[0]}
                </span>
                {!collapsed && (
                  <>
                    <span className="min-w-0 flex-1">
                      <span className="block truncate text-[13px] font-semibold leading-tight">
                        {user.firstName} {user.lastName}
                      </span>
                      <span className="block truncate text-[12px] text-muted-foreground">
                        {user.email}
                      </span>
                    </span>
                    {/* El rol conserva su color de siempre; cambia sólo la forma. */}
                    <span
                      title={user.role}
                      className={cn(
                        'shrink-0 rounded-md px-1.5 py-0.5 text-[10px] font-bold uppercase tracking-wider',
                        isAdmin ? 'bg-bloqueo-suave text-bloqueo' : 'bg-primary/10 text-primary'
                      )}
                    >
                      {ROL_CORTO[user.role] ?? user.role}
                    </span>
                  </>
                )}
              </button>
            </MenuDeLaCuenta>
          </div>
        )}
      </aside>
    </>
  );
}

// ── NavGroupItem ─────────────────────────────────────────────────

interface NavGroupItemProps {
  group: NavGroup;
  children?: NavChild[];
  hasChildren: boolean;
  isOpen: boolean;
  isGroupActive: boolean;
  collapsed: boolean;
  pathname: string;
  /** Query string actual — hace falta para distinguir enlaces con `?tab=`. */
  search: string;
  onToggle: () => void;
  /** El único destino que se pinta activo. Ver `resolverDestinoActivo`. */
  destinoActivo: string | null;
}

/**
 * El ícono de una entrada de primer nivel. Los módulos lo llevan en un cuadro
 * (relleno cuando se está adentro); el resto, suelto, en el mismo ancho para
 * que todos los textos arranquen en la misma columna.
 */
function IconoDeEntrada({
  icon: Icon,
  modulo,
  activo,
  colorModulo,
}: {
  icon: any;
  modulo?: boolean;
  activo?: boolean;
  colorModulo?: Modulo;
}) {
  if (!modulo) {
    return (
      <span
        className={cn(
          'grid h-8 w-8 shrink-0 place-items-center',
          activo ? 'text-primary-foreground' : 'text-muted-foreground'
        )}
      >
        <Icon className="h-[18px] w-[18px]" />
      </span>
    );
  }
  return (
    <span
      className={cn(
        'grid h-8 w-8 shrink-0 place-items-center rounded-lg transition-[box-shadow] duration-snap ease-snap',
        // Rediseño del 18-oct (artefacto): siempre relleno, con el color de su módulo.
        colorModulo ? CUADRO_DE_MODULO[colorModulo] : 'bg-primary text-primary-foreground',
        activo && 'shadow-sm ring-2 ring-primary/25'
      )}
    >
      <Icon className="h-4 w-4" aria-hidden="true" />
    </span>
  );
}

function NavGroupItem({
  group,
  children,
  hasChildren,
  isOpen,
  isGroupActive,
  collapsed,
  pathname,
  search,
  onToggle,
  destinoActivo,
}: NavGroupItemProps) {
  // Simple link (no children)
  if (!hasChildren) {
    const isActive = group.href === destinoActivo;
    return (
      <NavLink
        href={group.href!}
        icon={group.icon}
        label={group.label}
        badge={group.badge}
        description={group.description}
        isActive={isActive}
        collapsed={collapsed}
        menuKey={group.menuKey}
        modulo={group.modulo}
      />
    );
  }

  // Group with children
  const groupButton = (
    <button
      type="button"
      onClick={onToggle}
      // Dice si el grupo está abierto (lectores de pantalla y pruebas). Colapsado, el grupo abre su menú flotante.
      aria-expanded={collapsed ? undefined : isOpen}
      className={cn(
        /*
          Responde al presionarlo con FONDO y no con `scale` (plan 175 · B3): una
          fila de ancho completo no es una superficie que se pueda hundir.
        */
        'w-full flex items-center gap-3 rounded-xl transition-colors duration-snap ease-snap text-[14px] font-medium',
        'hover:bg-muted active:bg-muted/70',
        collapsed ? 'justify-center p-1.5' : 'px-1.5 py-1.5',
        isGroupActive && 'font-semibold'
      )}
    >
      {/*
        🔴 Un grupo que no es módulo (Mi empresa, Datos Maestros…) no tiene píldora:
        con `activo` su ícono se pintaba blanco sobre blanco y desaparecía al
        entrar a una de sus pantallas. Encontrado en el rediseño del 18-oct.
      */}
      <IconoDeEntrada
        icon={group.icon}
        modulo={group.modulo}
        activo={group.modulo ? isGroupActive : false}
        colorModulo={group.colorModulo}
      />
      {!collapsed && (
        <>
          <span className="flex-1 min-w-0 truncate text-left" title={group.label}>
            {group.label}
          </span>
          {group.badge !== undefined && group.badge > 0 && (
            <Badge variant="destructive" className="rounded-full text-[11px] mr-1">
              {group.badge}
            </Badge>
          )}
          <ChevronDown
            className={cn(
              'w-4 h-4 text-muted-foreground transition-transform duration-200',
              isOpen && 'rotate-180'
            )}
          />
        </>
      )}
    </button>
  );

  return (
    <div>
      {collapsed ? (
        <Tooltip>
          <TooltipTrigger asChild>{groupButton}</TooltipTrigger>
          <TooltipContent side="right" className="space-y-1">
            <p className="font-medium">{group.label}</p>
            {children?.flatMap((c) =>
              c.children && c.children.length > 0
                ? c.children.map((sc) => (
                    <HijoDelGrupoColapsado
                      key={sc.href}
                      href={sc.href}
                      menuKey={sc.menuKey ?? c.menuKey}
                    >
                      {c.label} / {sc.label}
                    </HijoDelGrupoColapsado>
                  ))
                : [
                    <HijoDelGrupoColapsado key={c.href} href={c.href} menuKey={c.menuKey}>
                      {c.label}
                    </HijoDelGrupoColapsado>,
                  ]
            )}
          </TooltipContent>
        </Tooltip>
      ) : (
        <>
          {groupButton}
          <AnimatePresence initial={false}>
            {isOpen && (
              <motion.div
                initial={{ height: 0, opacity: 0 }}
                animate={{ height: 'auto', opacity: 1 }}
                exit={{ height: 0, opacity: 0 }}
                transition={{ duration: 0.2 }}
                className="overflow-hidden"
              >
                {/*
                  Los hijos arrancan en la columna del texto del grupo (ícono de
                  32 px + 12 de separación), sin la línea guía de antes.
                */}
                <div className="pl-[38px] space-y-0.5 pt-1 pb-2">
                  {children?.map((child) => {
                    // Etiqueta de etapa: agrupa por momento del trabajo
                    // ("día a día" / "configuración") sin un nivel más de anidamiento.
                    const stage = child.stageLabel ? (
                      <p
                        key={`stage-${child.stageLabel}`}
                        className="px-3 pb-1 pt-3 text-[10.5px] font-semibold uppercase tracking-[0.08em] text-muted-foreground first:pt-1"
                      >
                        {child.stageLabel}
                      </p>
                    ) : null;

                    if (child.children && child.children.length > 0) {
                      return (
                        <div key={child.label}>
                          {stage}
                          <NavSubGroup
                            parent={child}
                            subChildren={child.children}
                            pathname={pathname}
                            search={search}
                            destinoActivo={destinoActivo}
                          />
                        </div>
                      );
                    }
                    const isActive = child.href === destinoActivo;
                    return (
                      <div key={child.href}>
                        {stage}
                        <NavLink
                          href={child.href}
                          icon={child.icon}
                          label={child.label}
                          badge={child.badge}
                          badgeTone={child.badgeTone}
                          description={child.description}
                          isActive={isActive}
                          collapsed={false}
                          compact
                          menuKey={child.menuKey}
                        />
                      </div>
                    );
                  })}
                </div>
              </motion.div>
            )}
          </AnimatePresence>
        </>
      )}
    </div>
  );
}

// ── NavSubGroup ───────────────────────────────────────────────────
// Sub-grupo expandible dentro de un grupo (un nivel mas profundo que NavGroupItem).
// Se auto-expande si alguno de sus sub-children es la ruta activa.

interface NavSubGroupProps {
  parent: NavChild;
  subChildren: NavChild[];
  pathname: string;
  search: string;
  destinoActivo: string | null;
}

function NavSubGroup({ parent, subChildren, pathname, search, destinoActivo }: NavSubGroupProps) {
  const hasActiveChild = subChildren.some((c) => isRouteActive(c.href, pathname, search));
  const [open, setOpen] = useState(hasActiveChild);
  // Mantener abierto si una sub-ruta esta activa, aunque el usuario haya colapsado manualmente
  const isOpen = open || hasActiveChild;

  return (
    <div>
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        aria-expanded={isOpen}
        className={cn(
          // Misma fila de navegación, mismo criterio: fondo al presionar, no escala.
          'w-full flex items-center gap-2 px-3 py-1.5 rounded-lg transition-colors duration-snap ease-snap text-[13px]',
          'hover:bg-muted active:bg-muted/70',
          hasActiveChild && 'text-primary font-medium'
        )}
      >
        <span className="flex-1 text-left whitespace-nowrap overflow-hidden">{parent.label}</span>
        <ChevronDown
          className={cn(
            'w-3.5 h-3.5 text-muted-foreground transition-transform duration-200',
            isOpen && 'rotate-180'
          )}
        />
      </button>
      <AnimatePresence initial={false}>
        {isOpen && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: 'auto', opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.2 }}
            className="overflow-hidden"
          >
            <div className="pl-3 space-y-0.5 py-0.5">
              {subChildren.map((sc) => {
                const isActive = sc.href === destinoActivo;
                return (
                  <NavLink
                    key={sc.href}
                    href={sc.href}
                    icon={sc.icon}
                    label={sc.label}
                    badge={sc.badge}
                    description={sc.description}
                    isActive={isActive}
                    collapsed={false}
                    compact
                    menuKey={sc.menuKey}
                  />
                );
              })}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}

// ── NavLink ──────────────────────────────────────────────────────

interface NavLinkProps {
  href: string;
  icon: any;
  label: string;
  badge?: number;
  /** `warn` pinta el contador en ámbar: es lo que hay que atender. */
  badgeTone?: 'neutral' | 'warn';
  description?: string;
  isActive: boolean;
  collapsed: boolean;
  /** Entrada hija: sin ícono, letra de 13 px. */
  compact?: boolean;
  menuKey?: string;
  /** Entrada de primer nivel que es un módulo: ícono en cuadro. */
  modulo?: boolean;
}

/** ¿La entrada está bloqueada (candado)? Incluye lo que la demo no trae (plan completo). */
function useModuloBloqueado(menuKey?: string, href = ''): boolean {
  return useAuthStore(
    (s) =>
      !!menuKey &&
      s.user?.role !== 'PLATFORM_ADMIN' &&
      (s.lockedMenuItems.includes(`menu:${menuKey}`) ||
        // Mini Prixia: fuera de lo que la demo incluye = parte del plan completo; y
        // «Modelos», que comparte la llave de Compite (decisión 7 de Sebastián).
        (esEmpresaDemo() &&
          // «Mi empresa» queda abierta en la demo (entra por Usuarios; ver empresa-demo.ts).
          !esEntradaAbiertaDeLaDemo(href) &&
          (!s.menuItems.includes(`menu:${menuKey}`) || esRutaDelPlanCompletoEnLaDemo(href))))
  );
}

/**
 * Un hijo del grupo colapsado (X1): en la demo, uno bloqueado es un botón que abre el
 * cuadro del plan completo encima de la pantalla actual; si no, el enlace de siempre.
 */
function HijoDelGrupoColapsado({
  href,
  menuKey,
  children,
}: {
  href: string;
  menuKey?: string;
  children: ReactNode;
}) {
  const bloqueado = useModuloBloqueado(menuKey, href) && esEmpresaDemo();
  const abrirPlanCompleto = usePlanCompleto((s) => s.abrir);
  if (bloqueado) {
    return (
      <button
        type="button"
        onClick={abrirPlanCompleto}
        className="block w-full text-left text-xs hover:underline"
      >
        {children}
        <span className="sr-only"> Parte del plan completo</span>
      </button>
    );
  }
  return (
    <Link to={href} className="block text-xs hover:underline">
      {children}
    </Link>
  );
}

function NavLink({
  href,
  icon,
  label,
  badge,
  badgeTone = 'neutral',
  description,
  isActive,
  collapsed,
  compact,
  menuKey,
  modulo,
}: NavLinkProps) {
  // Módulo visible pero bloqueado (estado "Bloqueado" de roles personalizados):
  // se muestra con candado; el clic navega y el guard de ruta corta el acceso.
  const locked = useModuloBloqueado(menuKey, href);
  // Si el href es absoluto (http/https), abrimos en nueva pestaña como link
  // externo en lugar de usar el router interno.
  // Mini Prixia (X1, decisión 24): en la demo, una entrada con candado NO navega (ni
  // abre un enlace externo, I3): es un botón que abre el cuadro del plan completo
  // encima de la pantalla actual. «Parte del plan completo» se dice también para el
  // lector de pantalla y al pasar el mouse (el candado solo es un dibujo).
  const bloqueadoEnLaDemo = locked && esEmpresaDemo();
  const abrirPlanCompleto = usePlanCompleto((s) => s.abrir);
  const isExternal = /^https?:\/\//i.test(href) && !bloqueadoEnLaDemo;
  const LinkComponent: any = bloqueadoEnLaDemo ? 'button' : isExternal ? 'a' : Link;
  const linkProps: any = bloqueadoEnLaDemo
    ? {
        type: 'button',
        onClick: abrirPlanCompleto,
        title: 'Parte del plan completo',
        className: 'block w-full text-left',
      }
    : isExternal
      ? { href, target: '_blank', rel: 'noopener noreferrer' }
      : // La pantalla abierta también para el lector de pantalla, no sólo por el color.
        { to: destinoEnLaDemo(href), 'aria-current': isActive ? 'page' : undefined };
  const content = (
    <LinkComponent {...linkProps}>
      <div
        className={cn(
          'relative flex items-center transition-colors duration-snap ease-snap',
          /*
            La letra blanca va SIEMPRE junto a la pastilla azul, nunca sola.
            Si un cambio futuro vuelve a dejar dos entradas activas, el peor caso
            es que se vean dos pastillas —feo pero legible— en vez de una entrada
            invisible.
          */
          isActive ? 'text-primary-foreground dark:text-foreground' : 'hover:bg-muted',
          // Mini Prixia: en la demo lo bloqueado se ve en gris, como «parte del plan completo».
          // (Una empresa común no cambia: sus bloqueos por rol se ven como siempre.)
          // Gris pleno: al 70 % quedaba ≈3:1 sobre blanco (pide 4,5:1).
          bloqueadoEnLaDemo && !isActive && 'text-muted-foreground',
          collapsed
            ? 'justify-center p-1.5 rounded-xl'
            : compact
              ? 'px-3 py-[7px] rounded-lg'
              : 'px-1.5 py-1.5 rounded-xl'
        )}
      >
        {isActive && (
          <motion.div
            layoutId="sidebar-active"
            className={cn(
              'absolute inset-0 bg-primary bg-degradado-prixia shadow-[0_4px_14px_-4px_hsl(var(--primary)/0.55)]',
              compact ? 'rounded-lg' : 'rounded-xl'
            )}
            transition={{ type: 'spring', stiffness: 350, damping: 30 }}
          />
        )}
        <span className={cn('relative z-10 flex items-center w-full', compact ? 'gap-2' : 'gap-3')}>
          {/* Las hijas van sin ícono, como en el boceto: el texto alcanza. */}
          {!compact && <IconoDeEntrada icon={icon} modulo={modulo} activo={isActive} />}
          {!collapsed && (
            <span
              className={cn(
                'flex-1 whitespace-nowrap overflow-hidden',
                compact ? 'text-[13px]' : 'text-[14px] font-medium',
                isActive && 'font-semibold'
              )}
            >
              {label}
            </span>
          )}
          {!collapsed && badge !== undefined && badge > 0 && (
            <span
              className={cn(
                'relative z-10 min-w-[22px] rounded-full px-1.5 py-0.5 text-center text-[11px] font-semibold tabular-nums',
                // En oscuro, la tinta sobre el dorado al 20 % no se leía: va el dorado (≈8:1).
                badgeTone === 'warn'
                  ? 'bg-prixia-gold/20 text-prixia-ink dark:bg-prixia-gold/25 dark:text-prixia-gold'
                  : 'bg-primary/15 text-primary',
                isActive && 'bg-white/20 text-primary-foreground'
              )}
            >
              {badge}
            </span>
          )}
          {locked && (
            <Lock
              className={cn('flex-shrink-0 opacity-60', collapsed ? 'w-3 h-3' : 'w-3.5 h-3.5')}
            />
          )}
          {bloqueadoEnLaDemo && <span className="sr-only">Parte del plan completo</span>}
          {!collapsed && isExternal && (
            <ExternalLink className="w-3 h-3 opacity-60 flex-shrink-0" />
          )}
        </span>
      </div>
    </LinkComponent>
  );

  if (collapsed) {
    return (
      <Tooltip>
        <TooltipTrigger asChild>{content}</TooltipTrigger>
        <TooltipContent side="right">
          <p className="font-medium">{label}</p>
          {description && <p className="text-xs opacity-80">{description}</p>}
        </TooltipContent>
      </Tooltip>
    );
  }

  return (
    <Tooltip>
      <TooltipTrigger asChild>{content}</TooltipTrigger>
      <TooltipContent side="right" className="max-w-xs">
        {description}
      </TooltipContent>
    </Tooltip>
  );
}

export default Sidebar;
